import React from 'react';
import { Paperclip, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../../context/UserAuthContext';

const MacSyncModal = ({
  isOpen,
  onClose,
  onImportSuccess,
  apiUrl
}) => {
  const userId = useUserId();
  
  if (!isOpen) return null;

  const baseUrl = apiUrl || process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const handleImportJson = async () => {
    const textarea = document.getElementById('mac-sync-json-input');
    const jsonText = textarea?.value?.trim();
    
    if (!jsonText) {
      toast.error('Please paste the JSON content first');
      return;
    }
    
    try {
      const data = JSON.parse(jsonText);
      
      // Validate structure
      if (!data.conversations && !data.messages) {
        toast.error('Invalid JSON format. Must contain conversations or messages.');
        return;
      }
      
      toast.loading('Importing messages...');
      
      const response = await fetch(`${baseUrl}/api/mac-messages-bridge/import-json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: jsonText
      });
      
      toast.dismiss();
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Imported ${result.conversations_synced} conversations, ${result.new_messages} new messages!`);
        textarea.value = '';
        onImportSuccess?.();
        onClose();
      } else {
        toast.error(`Import failed: HTTP ${response.status}`);
      }
    } catch (e) {
      toast.error('Invalid JSON format. Please check the file contents.');
    }
  };

  const handleFileUpload = async (files) => {
    if (files.length === 0) return;
    
    toast.loading(`Uploading ${files.length} files...`);
    let uploaded = 0;
    let failed = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', userId);
        
        const response = await fetch(`${baseUrl}/api/attachments/upload-file`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          uploaded++;
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
      }
    }
    
    toast.dismiss();
    if (uploaded > 0) {
      toast.success(`Uploaded ${uploaded} files${failed > 0 ? `, ${failed} failed` : ''}`);
    } else {
      toast.error(`Failed to upload files`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-700 rounded-lg">
              <svg className="w-6 h-6 text-gray-300" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Sync from Mac</h3>
              <p className="text-sm text-gray-400">Import iMessages from your Mac computer</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* How it works */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              How it works
            </h4>
            <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
              <li>Download the Python script to your Mac</li>
              <li>Grant Terminal &quot;Full Disk Access&quot; in System Settings</li>
              <li>Run: <code className="bg-black px-1 rounded">python3 mac_sync.py --export messages.json</code></li>
              <li>Paste the JSON content below OR upload the file</li>
            </ol>
          </div>
          
          {/* Terminal Commands */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">Terminal Commands</h4>
            <div className="bg-black rounded-lg p-3 font-mono text-xs text-green-400 overflow-x-auto">
              <p className="text-gray-500"># Download the script</p>
              <p>curl -O {baseUrl}/api/static/mac_sync.py</p>
              <p className="text-gray-500 mt-2"># Export messages to JSON file</p>
              <p>python3 mac_sync.py --export messages.json</p>
            </div>
          </div>
          
          {/* JSON Paste Section */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <h4 className="text-purple-400 font-semibold mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Upload JSON (if sync fails with 403)
            </h4>
            <p className="text-xs text-gray-400 mb-3">
              Run <code className="bg-black px-1 rounded">python3 mac_sync.py --export messages.json</code> then paste the file contents here:
            </p>
            <textarea
              id="mac-sync-json-input"
              placeholder='Paste JSON here or open messages.json and copy its contents...'
              className="w-full h-24 bg-black border border-gray-600 rounded-lg p-3 text-xs text-gray-300 font-mono resize-none focus:border-purple-500 focus:outline-none"
              data-testid="mac-sync-json-textarea"
            />
            <button
              onClick={handleImportJson}
              className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm"
              data-testid="import-mac-json-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Import Messages
            </button>
          </div>
          
          {/* Download buttons */}
          <div className="flex gap-3">
            <a
              href={`${baseUrl}/api/static/mac_sync.py`}
              download="mac_sync.py"
              className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm"
              data-testid="download-mac-sync-script-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Script
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`curl -O ${baseUrl}/api/static/mac_sync.py && python3 mac_sync.py --export messages.json`);
                toast.success('Command copied to clipboard!');
              }}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-sm"
              data-testid="copy-mac-sync-command-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Command
            </button>
          </div>
          
          {/* Warning note */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-yellow-400 text-xs flex items-start gap-2">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>
                <strong>Note:</strong> Grant Terminal &quot;Full Disk Access&quot; in macOS System Settings → Privacy & Security before running the script.
              </span>
            </p>
          </div>
          
          {/* Attachment Upload Section */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <h4 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Upload Attachments (Photos, Videos, Files)
            </h4>
            <p className="text-xs text-gray-400 mb-3">
              Drag & drop or select files to upload. These will be linked to your synced messages.
            </p>
            <div 
              className="border-2 border-dashed border-green-500/50 rounded-lg p-6 text-center hover:border-green-400 transition-colors cursor-pointer"
              onClick={() => document.getElementById('attachment-upload-input')?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-green-400', 'bg-green-500/10'); }}
              onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10'); }}
              onDrop={async (e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                const files = Array.from(e.dataTransfer.files);
                handleFileUpload(files);
              }}
            >
              <ImageIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-gray-300">Drop files here or click to select</p>
              <p className="text-xs text-gray-500 mt-1">Supports images, videos, PDFs, audio</p>
            </div>
            <input 
              type="file" 
              id="attachment-upload-input" 
              className="hidden" 
              multiple 
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                handleFileUpload(files);
                e.target.value = ''; // Reset input
              }}
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MacSyncModal;
